import { ArrowUpRight, MapPin } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const HERO_IMG =
  "/img/office.webp";

export const Hero = () => {
  const ref = useReveal();
  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-[8.5rem] lg:pt-[9.5rem]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Top eyebrow line */}
        <div className="flex items-center justify-between text-xs tracking-[0.22em] uppercase text-black/55 pt-6 pb-10 border-b hairline reveal">
          <span data-testid="hero-eyebrow-location" className="inline-flex items-center gap-2">
            <MapPin size={12} /> Scotland · UK-wide delivery
          </span>
          <span className="hidden sm:inline">Est. Anderston Group</span>
          <span className="inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full live-dot" />
            Open · Mon–Fri
          </span>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-12 gap-6 pt-14 lg:pt-20">
          <div className="col-span-12 lg:col-span-8 reveal">
            <h1
              data-testid="hero-headline"
              className="font-display text-[44px] sm:text-[64px] lg:text-[92px] xl:text-[108px] leading-[0.92] tracking-[-0.03em] font-medium text-balance"
            >
              Design-led
              <br />
              interior fit-outs,
              <br />
              <span className="italic font-normal text-black/70">
                built in-house.
              </span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pt-4 reveal">
            <p
              data-testid="hero-subhead"
              className="text-base lg:text-lg text-black/65 leading-relaxed max-w-md"
            >
              Anderston Group delivers considered interiors for workspaces,
              bars, restaurants and distilleries. Every trade carried in-house,
              led by people who care about the detail — and sharpened by an
              AI-native project engine.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <a
                href="#contact"
                data-testid="hero-cta-start"
                className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-medium bg-[#0A0A0A] text-white hover:bg-black/85 transition-colors"
              >
                Start a project <ArrowUpRight size={16} />
              </a>
              <a
                href="#capabilities"
                data-testid="hero-cta-explore"
                className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-medium border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                Explore capabilities
              </a>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="mt-16 lg:mt-24 border-y hairline overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap py-5">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-12 pr-12 text-sm tracking-[0.22em] uppercase text-black/70">
                <span>Partitioning</span><span className="text-black/25">/</span>
                <span>M&E</span><span className="text-black/25">/</span>
                <span>Flooring</span><span className="text-black/25">/</span>
                <span>Painting</span><span className="text-black/25">/</span>
                <span>Manufactured Joinery</span><span className="text-black/25">/</span>
                <span>Solid Surface</span><span className="text-black/25">/</span>
                <span>Metalwork</span><span className="text-black/25">/</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hero image panel */}
        <div className="mt-10 lg:mt-14 grid grid-cols-12 gap-6 reveal">
          <div className="col-span-12 lg:col-span-9 relative">
            <div className="relative aspect-[16/9] lg:aspect-[21/9] w-full overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Considered interior environment"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <div className="overline group-hover:text-white/70">Capability snapshot</div>
                <div className="font-display text-2xl lg:text-3xl mt-2">Workspace · Hospitality · Distillery</div>
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-3 flex flex-col justify-between gap-6">
            <Stat label="In-house trades" value="07" testid="hero-stat-trades" />
            <Stat label="Workshop floor" value="12,000 ft²" testid="hero-stat-workshop" />
            <Stat label="Our approach" value="Craft + AI" testid="hero-stat-ai" />
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="border hairline p-5 lg:p-6 flex flex-col justify-between min-h-[110px] hover:-translate-y-0.5 transition-transform"
  >
    <div className="overline">{label}</div>
    <div className="font-display text-3xl lg:text-4xl tracking-tight mt-3">
      {value}
    </div>
  </div>
);

export default Hero;
